import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { UserPlus, Flag, FileText, KeyRound, LogOut } from "lucide-react";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  startGetUserLanguage,
  startUpdateUserLanguage,
} from "../../actions/adsCampaigns";
import ButtonCreateAccountFromAnonymous from "../Buttons/ButtonCreateAccountFromAnonymous";
import {
  BUY_CREDITS,
  HELP,
  LOGOUT,
  OWNER_DASHBOARD_NAV_CREATE_USER,
  OWNER_DASHBOARD_NAV_FORM,
  OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES,
  OWNER_DASHBOARD_NAV_REPORTED_CONTENT,
  SUPPORT,
} from "../../i18n/keysTranslations";
import { startLogout } from "../../actions/auth";
import { openTidioChat } from "../../hooks/useTidio";
import { getNameBusinessTheme } from "../../actions/getters";
import { Link, useLocation } from "react-router-dom";
import { IconButton, useMediaQuery } from "@mui/material";
import { useState, useEffect } from "react";
import {
  PADDING_DESKTOP,
  PADDING_MOBILE,
  OWNER_ROLE,
  PLAN_TYPE_CREDITS,
  FONT_WEIGHT_BUTTON,
  FONT_SIZE_BUTTON,
} from "../../utils/constants";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import { setOpenSlideMenu, setModalOpenState } from "../../actions/ui";
import MenuIcon from "@mui/icons-material/Menu";
import { HelpCircle, Headset } from "lucide-react";
import SlideMenu from "../Menus/SlideMenu";
import UsageCounter from "../Billing/UsageCounter";
import { SelectAdvertiserID } from "../Form/SelectAdvertiserID";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import i18next from "i18next";
import LanguageSwitcher from "../Common/LanguageSwitcher";
import GlobalNotificationsAlert from "../Alerts/GlobalNotificationsAlert";
import ModalCreateOwnerUser from "../../pages/AdminDashboard/components/ModalCreateOwnerUser";
import ModalAdHocSupport from "../Modal/ModalAdHocSupport";

const ROUTES_WITHOUT_SLIDE_MENU = ["/ads-campaign", "/ads-campaign-statistics"];

const AD_HOC_SUPPORT_MIN_PLAN_PRICE = 499;
const OWNER_NAV_ICON_SIZE = 20;
const NAV_ICON_SIZE = 20;
const NAV_ICON_STROKE_WIDTH = 2;

const OwnerNavIconAction = ({
  icon: Icon,
  label,
  onClick = () => {},
  to = null,
}) => {
  const iconButtonSx = {
    color: "inherit",
  };

  const iconContent = <Icon size={OWNER_NAV_ICON_SIZE} />;

  if (to) {
    return (
      <Tooltip title={label}>
        <IconButton
          component={Link}
          to={to}
          aria-label={label}
          sx={iconButtonSx}
        >
          {iconContent}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={label}>
      <IconButton onClick={onClick} aria-label={label} sx={iconButtonSx}>
        {iconContent}
      </IconButton>
    </Tooltip>
  );
};

const NavBar = () => {
  const dispatch = useDispatchApp();
  const { theme, slideMenuIsOpen } = useSelectorApp((state) => state.ui);
  const { user } = useSelectorApp((state) => state.auth);
  const { t } = useTranslationApp();
  const location = useLocation();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const [userLanguage, setUserLanguage] = useState(null);
  const [isModalCreateOwnerUserOpen, setIsModalCreateOwnerUserOpen] =
    useState(false);
  const [isModalAdHocSupportOpen, setIsModalAdHocSupportOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isOwner = user?.role === OWNER_ROLE;
  const showMobileNavToggle = !isOwner && isMobile;
  const { typeOfPlans, subscriptionPlan, subscriptionPlanInfo } =
    useSelectorApp((state) => state.payments);
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const isAnonymous = user?.isAnonymous;
  const showBuyCreditsButton =
    !!subscriptionPlan &&
    typeOfPlans === PLAN_TYPE_CREDITS &&
    !user.isAnonymous;
  const showAdHocSupportButton =
    !isAnonymous &&
    !isOwner &&
    !isAppSumo &&
    isAppSumoResolved &&
    !!subscriptionPlan &&
    Number(subscriptionPlanInfo?.price) >= AD_HOC_SUPPORT_MIN_PLAN_PRICE;

  const { navbarTop } = useRebrandBanner();

  // Get user language when component loads
  useEffect(() => {
    const getUserLanguage = async () => {
      if (user?.uid) {
        try {
          const language = await dispatch(startGetUserLanguage());
          if (language) {
            setUserLanguage(language);
            i18next.changeLanguage(language);
          } else {
            const browserLanguage =
              navigator.language || navigator.languages?.[0] || "en";
            const supportedLanguage = browserLanguage.startsWith("es")
              ? "es"
              : "en";
            setUserLanguage(supportedLanguage);
            i18next.changeLanguage(supportedLanguage);
          }
        } catch (error) {
          console.error("Error al obtener idioma del usuario:", error);
          const browserLanguage =
            navigator.language || navigator.languages?.[0] || "en";
          const supportedLanguage = browserLanguage.startsWith("es")
            ? "es"
            : "en";
          setUserLanguage(supportedLanguage);
          i18next.changeLanguage(supportedLanguage);
        }
      }
    };

    getUserLanguage();
  }, [dispatch, user?.uid]);

  const changeLanguage = async (language) => {
    try {
      i18next.changeLanguage(language);
      setUserLanguage(language);
      await dispatch(startUpdateUserLanguage(language));
    } catch (error) {
      console.error("Error al cambiar idioma:", error);
    }
  };

  const onLogout = () => {
    dispatch(startLogout());
  };

  const onOpenModalCreateOwnerUser = () => {
    setIsModalCreateOwnerUserOpen(true);
  };

  const onCloseModalCreateOwnerUser = () => {
    setIsModalCreateOwnerUserOpen(false);
  };

  const onOpenAdHocSupport = () => {
    setIsModalAdHocSupportOpen(true);
  };

  const onCloseModalAdHocSupport = () => {
    setIsModalAdHocSupportOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          top: navbarTop,
          boxShadow: "none",
          backgroundImage: "none",
          backgroundColor: "background.paper",
          color: "background.paperContrastText",
          borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
          pb: 1,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            px: {
              md: PADDING_DESKTOP,
              xs: PADDING_MOBILE,
            },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pt: 1,
            }}
          >
            {showMobileNavToggle && (
              <IconButton
                size="large"
                edge="start"
                onClick={() => dispatch(setOpenSlideMenu(!slideMenuIsOpen))}
                sx={{
                  mt: 0.3,
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link
              to="/"
              style={{
                display: "flex",
                width: isOwner ? "auto" : "65%",
                justifyContent: "flex-start",
              }}
            >
              <Avatar
                src={theme.logo}
                alt={`Logo ${NAME_BUSINESS}`}
                variant="square"
                sx={{
                  width: "60px",
                  mb: { xs: 0.2, md: 0 },
                  ".MuiAvatar-img": {
                    maxWidth: "105px",
                    objectFit: "contain",
                  },
                }}
              />
            </Link>
          </Box>
          {!isOwner && isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pt: 1,
              }}
            >
              <GlobalNotificationsAlert sx={{ mt: 0.2 }} />
            </Box>
          )}
          <Box
            className="buttons-nav"
            sx={{
              display: { xs: isOwner ? "flex" : "none", md: "flex" },
              alignItems: "center",
              gap: isOwner ? 0.5 : 1.5,
              pt: 1,
              flexShrink: 0,
            }}
          >
            {!isOwner && (
              <>
                {!isMobile && (
                  <>
                    {showBuyCreditsButton && (
                      <Box
                        onClick={() =>
                          dispatch(
                            setModalOpenState("isModalPurchaseCredits", true)
                          )
                        }
                        sx={{
                          display: "flex",
                          mt: 0.2,
                          gap: 1,
                          fontWeight: FONT_WEIGHT_BUTTON,
                          cursor: "pointer",
                          fontSize: FONT_SIZE_BUTTON,
                        }}
                      >
                        {t(BUY_CREDITS)}
                      </Box>
                    )}
                    <GlobalNotificationsAlert sx={{ mt: 0.2 }} />
                  </>
                )}
                <UsageCounter />
              </>
            )}

            {isOwner && (
              <>
                <OwnerNavIconAction
                  icon={UserPlus}
                  label={t(OWNER_DASHBOARD_NAV_CREATE_USER)}
                  onClick={onOpenModalCreateOwnerUser}
                />
                <OwnerNavIconAction
                  icon={Flag}
                  label={t(OWNER_DASHBOARD_NAV_REPORTED_CONTENT)}
                  to="/report-content"
                />
                <OwnerNavIconAction
                  icon={FileText}
                  label={t(OWNER_DASHBOARD_NAV_FORM)}
                  to="/leads"
                />
                <OwnerNavIconAction
                  icon={KeyRound}
                  label={t(OWNER_DASHBOARD_NAV_PROMOTIONAL_CODES)}
                  to="/promotional-codes"
                />
              </>
            )}

            {!isAnonymous && !isOwner && (
              <IconButton
                aria-label={t(HELP)}
                sx={{
                  p: 0,
                  mx: 1,
                  mt: 0.2,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
                onClick={openTidioChat}
              >
                <HelpCircle
                  size={NAV_ICON_SIZE}
                  strokeWidth={NAV_ICON_STROKE_WIDTH}
                />
              </IconButton>
            )}

            {showAdHocSupportButton && (
              <IconButton
                aria-label={t(SUPPORT)}
                sx={{
                  p: 0,
                  mr: 1,
                  mt: 0.2,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
                onClick={onOpenAdHocSupport}
              >
                <Headset
                  size={NAV_ICON_SIZE}
                  strokeWidth={NAV_ICON_STROKE_WIDTH}
                />
              </IconButton>
            )}

            <LanguageSwitcher
              language={userLanguage || "en"}
              onChangeLanguage={changeLanguage}
              colorButton="secondary"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              sx={{
                height: 42,
                maxHeight: 42,
                minWidth: "max-content",
                border: "none !important",
                boxShadow: "none !important",
                backgroundColor: "transparent !important",
                ...(isOwner
                  ? { px: "5px !important" }
                  : { pl: "12px !important", px: "0 !important" }),
                "&.MuiButtonBase-root.MuiButton-root:hover": {
                  backgroundColor: "transparent !important",
                  border: "none !important",
                  boxShadow: "none !important",
                  color: "inherit !important",
                },
              }}
              sxMenu={{
                "& .MuiPaper-root": {
                  marginTop: "8px",
                },
              }}
            />

            {isOwner ? (
              <OwnerNavIconAction
                icon={LogOut}
                label={t(LOGOUT)}
                onClick={onLogout}
              />
            ) : null}

            {isAnonymous ? (
              <ButtonCreateAccountFromAnonymous
                color="secondary"
                hasShadow={false}
                sx={{
                  height: 42,
                  maxHeight: 42,
                  minWidth: "max-content",
                  border: "none",
                  boxShadow: "none",
                  px: "10px !important",
                  "&.MuiButtonBase-root.MuiButton-root:hover": {
                    backgroundColor: "transparent !important",
                    border: "none !important",
                    boxShadow: "none !important",
                    color: "inherit !important",
                  },
                }}
              />
            ) : null}
            {!isOwner && (
              <Box id="navbar-advertiser-selector">
                <SelectAdvertiserID sx={{ width: 185 }} />
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {!isOwner && !ROUTES_WITHOUT_SLIDE_MENU.includes(location.pathname) && (
        <SlideMenu
          onClickBuyCredits={() =>
            dispatch(setModalOpenState("isModalPurchaseCredits", true))
          }
          showBuyCreditsButton={showBuyCreditsButton}
        />
      )}
      {isOwner && (
        <ModalCreateOwnerUser
          modalOpen={isModalCreateOwnerUserOpen}
          onCloseModal={onCloseModalCreateOwnerUser}
        />
      )}
      {showAdHocSupportButton && (
        <ModalAdHocSupport
          modalOpen={isModalAdHocSupportOpen}
          onCloseModal={onCloseModalAdHocSupport}
        />
      )}
    </>
  );
};

export default NavBar;
